import {
    FunctionComponent,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import {
    WELL_DETAILS_ABS_ROUTE,
    WELL_NOTES_ABS_ROUTE,
    WELL_NOTES_UPDATE_CHILD_ROUTE,
} from "../../../.."
import Button from "../../../shared/button"
import {
    WellNoteVisibility,
    WellNotesForIwpFieldsFragment,
    ZoneInfo,
} from "../../../../generated/graphql"
import { Link, useNavigate } from "react-router-dom"
import styles from "./list.module.scss"
import Fuse from "fuse.js"
import dayjs from "../../../../util/dayjs"
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import WellNotesListHeader from "./header"
import { sortArrayByKey } from "../../../../util/arrays/array"
import { WellDetailsQryParams } from "../../wellDetailsChart"
import { useViewerContext } from "../../../../context/ViewerContext"
import {
    getDisplayStringFromZoneInfo,
    getZoneInfoFromEnum,
} from "../../../../graphql/enum/zoneInfo"

// Types
export interface WellNotesFilterI {
    createdBy: string[]
    dateRange: {
        startDate: string
        endDate: string
    }
}

export interface WellNotesSortConfigI {
    field: "occurredAt"
    direction: "asc" | "desc"
}

interface WellNotesListPropsI {
    wellNotes: WellNotesForIwpFieldsFragment[]
}

const WELL_NOTES_READING_WIDTH_STORAGE_KEY = (subjectId: string): string => {
    return `wellNotesReadingWidth::${subjectId}`
}

// Component
const WellNotesList: FunctionComponent<WellNotesListPropsI> = ({
    wellNotes,
}) => {
    const { userId, identity } = useViewerContext().getViewer()
    const zoneInfo = identity.zoneInfo
    const tz = getZoneInfoFromEnum(zoneInfo)
    const profileTimezoneLabel = getDisplayStringFromZoneInfo(zoneInfo)

    // state
    const [searchInputTerm, setSearchInputTerm] = useState("")
    const [isReadingWidth, setIsReadingWidth] = useState<boolean>(() => {
        const key = WELL_NOTES_READING_WIDTH_STORAGE_KEY(userId)
        return localStorage.getItem(key) === "true"
    })

    const [sortConfig, setSortConfig] = useState<WellNotesSortConfigI>({
        field: "occurredAt",
        direction: "desc",
    })

    const [filters, setFilters] = useState<WellNotesFilterI>({
        createdBy: [],
        dateRange: {
            startDate: "",
            endDate: "",
        },
    })

    // Extract all unique users for filter dropdowns
    const uniqueUsers = useMemo(() => {
        const userMap = new Map<string, string>()

        wellNotes.forEach((note) => {
            // Add created by user
            if (note.createdBy.identity?.fullName) {
                userMap.set(
                    note.createdBy.id,
                    formatWellNoteActorName(note.createdBy.identity.fullName)
                )
            }
        })

        return Array.from(userMap.entries()).map(([id, name]) => ({ id, name }))
    }, [wellNotes])

    const parentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        localStorage.setItem(
            WELL_NOTES_READING_WIDTH_STORAGE_KEY(userId),
            String(isReadingWidth)
        )
    }, [isReadingWidth, userId])

    const fuse = useMemo(() => {
        return new Fuse(wellNotes, {
            keys: [
                "note",
                {
                    name: "createdByDisplayName",
                    getFn: (note) =>
                        formatWellNoteActorName(
                            note.createdBy.identity?.fullName || ""
                        ),
                },
                {
                    name: "updatedByDisplayName",
                    getFn: (note) =>
                        formatWellNoteActorName(
                            note.updatedBy.identity?.fullName || ""
                        ),
                },
                {
                    name: "occurredAt",
                    getFn: (note) =>
                        dayjs(note.occurredAt.unixMilliseconds)
                            .tz(tz)
                            .format("MMM D, YYYY h:mm A"),
                },
                {
                    name: "occurredUntil",
                    getFn: (note) =>
                        note.occurredUntil
                            ? dayjs(note.occurredUntil.unixMilliseconds)
                                  .tz(tz)
                                  .format("MMM D, YYYY h:mm A")
                            : "",
                },
            ],
            threshold: 0,
            ignoreLocation: true,
        })
    }, [wellNotes, tz])

    // Apply search, filter, and sort to get processed notes
    const processedNotes = useMemo(() => {
        // apply search if there's a search term
        let notes: WellNotesForIwpFieldsFragment[] = wellNotes

        if (searchInputTerm.trim()) {
            const searchResults = fuse.search(searchInputTerm)
            notes = searchResults.map((res) => res.item)
        }

        //  apply filters
        if (filters.createdBy.length > 0) {
            notes = notes.filter((note) =>
                filters.createdBy.includes(note.createdBy.id)
            )
        }

        // Apply date range filter
        if (filters.dateRange?.startDate) {
            // Use profile timezone boundaries, then compare in UTC epoch seconds.
            const startTimestamp = dayjs
                .tz(filters.dateRange.startDate, tz)
                .startOf("day")
                .valueOf()

            notes = notes.filter(
                (note) => note.occurredAt.unixMilliseconds >= startTimestamp
            )
        }

        if (filters.dateRange?.endDate) {
            // Use profile timezone boundaries, then compare in UTC epoch seconds.
            const endTimestamp = dayjs
                .tz(filters.dateRange.endDate, tz)
                .endOf("day")
                .valueOf()

            notes = notes.filter(
                (note) => note.occurredAt.unixMilliseconds <= endTimestamp
            )
        }

        // apply sort
        const sortedNotes = sortArrayByKey(
            notes,
            (note) => note.occurredAt.unixMilliseconds,
            false,
            sortConfig.direction
        )

        return sortedNotes
    }, [wellNotes, searchInputTerm, filters, sortConfig, fuse, tz])

    // Reset scroll position when filters change drastically
    const scrollToTop = useCallback(() => {
        if (parentRef.current) {
            parentRef.current.scrollTo({
                top: 0,
                behavior: "auto",
            })
        }
    }, [])

    return (
        <div
            className={`${styles.listLayout} ${
                isReadingWidth ? styles.readingWidth : ""
            }`}
        >
            <div className={styles.componentContianer}>
                <WellNotesListHeader
                    wellId={wellNotes[0].well.id}
                    searchTerm={searchInputTerm}
                    handleSearchTermChange={(value) => {
                        setSearchInputTerm(value)
                        scrollToTop()
                    }}
                    sortConfig={sortConfig}
                    handleSortConfigChange={(config) => {
                        setSortConfig(config)
                        scrollToTop()
                    }}
                    filters={filters}
                    onFilterChange={(newFilters) => {
                        setFilters(newFilters)
                        scrollToTop()
                    }}
                    uniqueUsers={uniqueUsers}
                    isReadingWidth={isReadingWidth}
                    onToggleReadingWidth={() =>
                        setIsReadingWidth((prev) => !prev)
                    }
                    profileTimezoneLabel={profileTimezoneLabel}
                    profileTimezoneIana={tz}
                    resetAll={() => {
                        setFilters({
                            createdBy: [],
                            dateRange: {
                                startDate: "",
                                endDate: "",
                            },
                        })
                        setSortConfig({
                            field: "occurredAt",
                            direction: "desc",
                        })
                        setSearchInputTerm("")
                        scrollToTop()
                    }}
                />
            </div>
            <div className={styles.componentContianer}>
                <WellNotesListSummary
                    filters={filters}
                    processedNotesCount={processedNotes.length}
                    searchInputTerm={searchInputTerm}
                    totalNotesCount={wellNotes.length}
                />
            </div>
            <div className={styles.componentContianer}>
                <List
                    processedNotes={processedNotes}
                    searchInputTerm={searchInputTerm}
                    parentRef={parentRef}
                    sortDirection={sortConfig.direction}
                    zoneInfo={zoneInfo}
                />
            </div>
        </div>
    )
}

export default WellNotesList

interface WellNotesListSummaryProps {
    searchInputTerm: string
    filters: {
        createdBy: string[]

        dateRange?: {
            startDate: string
            endDate: string
        }
    }
    processedNotesCount: number
    totalNotesCount: number
}

const WellNotesListSummary: FunctionComponent<WellNotesListSummaryProps> = ({
    searchInputTerm,
    filters,
    processedNotesCount,
    totalNotesCount,
}) => {
    const isEmpty = processedNotesCount === 0

    const hasFilters =
        searchInputTerm ||
        filters.createdBy.length > 0 ||
        (filters.dateRange &&
            (filters.dateRange.startDate || filters.dateRange.endDate))

    if (!hasFilters && !isEmpty) {
        return null
    }

    return (
        <div className={styles.summaryContainer}>
            {hasFilters && !isEmpty && (
                <div className={styles.resultCount}>
                    Showing {processedNotesCount} of {totalNotesCount} notes
                </div>
            )}
            {isEmpty && (
                <div className={styles.noResults}>
                    No notes match your search. Try adjusting your filters.
                </div>
            )}
        </div>
    )
}

interface ListPropsI {
    processedNotes: WellNotesForIwpFieldsFragment[]
    searchInputTerm: string
    parentRef: React.RefObject<HTMLDivElement>
    sortDirection: WellNotesSortConfigI["direction"]
    zoneInfo: ZoneInfo
}

type RecencySectionKey = "last7Days" | "last6Months" | "lastYear" | "older"

type NotesRecencySection = {
    key: RecencySectionKey
    label: string
    notes: WellNotesForIwpFieldsFragment[]
}

/**
 * Performance follow-up:
 * This grouped/collapsible implementation renders all expanded notes.
 * For large note volumes, migrate to a section-aware virtual list:
 * - Build a flat row model of section headers + visible note rows.
 * - Keep stable keys (`section:<key>`, `note:<id>`).
 * - Virtualize rows with @tanstack/react-virtual and measure variable heights.
 * - Recompute the row model with useMemo when filters/search/sort/collapse change.
 */
const List: FunctionComponent<ListPropsI> = ({
    processedNotes,
    searchInputTerm,
    parentRef,
    sortDirection,
    zoneInfo,
}) => {
    const tz = getZoneInfoFromEnum(zoneInfo)
    const [collapsedSections, setCollapsedSections] = useState<
        Record<RecencySectionKey, boolean>
    >({
        last7Days: false,
        last6Months: false,
        lastYear: false,
        older: false,
    })
    const groupedSections = useMemo(
        () => groupNotesByRecency(processedNotes, sortDirection, tz),
        [processedNotes, sortDirection, tz]
    )

    if (!processedNotes.length) {
        return (
            <div
                ref={parentRef}
                className={`${styles.listContainer} ${styles.listViewport}`}
            />
        )
    }

    return (
        // scrolable element
        <div
            ref={parentRef}
            className={`${styles.listContainer} ${styles.listViewport}`}
        >
            {groupedSections.map((section) => {
                const isCollapsed = collapsedSections[section.key]
                return (
                    <section key={section.key} className={styles.sectionGroup}>
                        <button
                            type="button"
                            className={styles.sectionHeader}
                            onClick={() => {
                                setCollapsedSections((prev) => ({
                                    ...prev,
                                    [section.key]: !prev[section.key],
                                }))
                            }}
                        >
                            <span className={styles.sectionCaret}>
                                <FontAwesomeIcon
                                    icon={
                                        isCollapsed
                                            ? faChevronRight
                                            : faChevronDown
                                    }
                                />
                            </span>
                            <span className={styles.sectionTitle}>
                                {section.label}
                            </span>
                            <span className={styles.sectionCount}>
                                {section.notes.length}
                            </span>
                        </button>
                        {!isCollapsed && (
                            <div className={styles.sectionNotes}>
                                {section.notes.map((wellNote) => {
                                    const unixMsEod = dayjs()
                                        .tz(tz)
                                        .endOf("day")
                                        .valueOf()
                                    const isFutureNote =
                                        wellNote.occurredAt.unixMilliseconds >
                                        unixMsEod
                                    return (
                                        <div
                                            key={wellNote.id}
                                            className={styles.noteCard}
                                        >
                                            <div className={styles.noteTopRow}>
                                                <OccurredAtDate
                                                    occurredAtUnixMs={
                                                        wellNote.occurredAt
                                                            .unixMilliseconds
                                                    }
                                                    occurredUntilUnixMs={
                                                        wellNote.occurredUntil
                                                            ?.unixMilliseconds
                                                    }
                                                    searchInputTerm={
                                                        searchInputTerm
                                                    }
                                                    wellId={wellNote.well.id}
                                                    isLink={!isFutureNote}
                                                    timezone={tz}
                                                />
                                                <div
                                                    className={
                                                        styles.noteTopRight
                                                    }
                                                >
                                                    {wellNote.visibility ===
                                                        WellNoteVisibility.Internal && (
                                                        <div
                                                            className={
                                                                styles.internalBadge
                                                            }
                                                        >
                                                            Internal
                                                        </div>
                                                    )}
                                                    <UpdateNoteBtn
                                                        wellId={
                                                            wellNote.well.id
                                                        }
                                                        noteId={wellNote.id}
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.noteContent}>
                                                {highlightSearchTerms(
                                                    wellNote.note,
                                                    searchInputTerm
                                                )}
                                            </div>
                                            <div className={styles.noteFooter}>
                                                <div
                                                    className={
                                                        styles.noteMetaItem
                                                    }
                                                >
                                                    Created by:{" "}
                                                    {highlightSearchTerms(
                                                        formatWellNoteActorName(
                                                            wellNote.createdBy
                                                                .identity
                                                                ?.fullName || ""
                                                        ),
                                                        searchInputTerm
                                                    )}
                                                </div>
                                                <div
                                                    className={
                                                        styles.noteMetaItem
                                                    }
                                                >
                                                    Updated by:{" "}
                                                    {highlightSearchTerms(
                                                        formatWellNoteActorName(
                                                            wellNote.updatedBy
                                                                .identity
                                                                ?.fullName || ""
                                                        ),
                                                        searchInputTerm
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </section>
                )
            })}
        </div>
    )
}

const groupNotesByRecency = (
    notes: WellNotesForIwpFieldsFragment[],
    direction: WellNotesSortConfigI["direction"],
    timezone: string
): NotesRecencySection[] => {
    const todayInTz = dayjs().tz(timezone).startOf("day")
    // Align recency groups to calendar-day boundaries in profile timezone.
    const last7DaysStartMs = todayInTz.subtract(6, "day").valueOf()
    const last6MonthsStartMs = todayInTz.subtract(6, "month").valueOf()
    const lastYearStartMs = todayInTz.subtract(1, "year").valueOf()

    const groups: Record<RecencySectionKey, WellNotesForIwpFieldsFragment[]> = {
        last7Days: [],
        last6Months: [],
        lastYear: [],
        older: [],
    }

    notes.forEach((note) => {
        const occurredMs = note.occurredAt.unixMilliseconds

        if (occurredMs >= last7DaysStartMs) {
            groups.last7Days.push(note)
            return
        }
        if (occurredMs >= last6MonthsStartMs) {
            groups.last6Months.push(note)
            return
        }
        if (occurredMs >= lastYearStartMs) {
            groups.lastYear.push(note)
            return
        }
        groups.older.push(note)
    })

    const sections: NotesRecencySection[] = [
        { key: "last7Days", label: "Last 7 Days", notes: groups.last7Days },
        {
            key: "last6Months",
            label: "Last 6 Months",
            notes: groups.last6Months,
        },
        { key: "lastYear", label: "Last Year", notes: groups.lastYear },
        { key: "older", label: "Older", notes: groups.older },
    ]

    const nonEmptySections = sections.filter(
        (section) => section.notes.length > 0
    )
    return direction === "asc" ? nonEmptySections.reverse() : nonEmptySections
}

// Components

// OccurredAtDate displays the date of the note and highlights the search term if present.
// It also provides a link to the well details page if isLink is true. If the note is a future date we do not want to display a link
const OccurredAtDate: FunctionComponent<{
    occurredAtUnixMs: number
    occurredUntilUnixMs?: number
    wellId: string
    searchInputTerm: string
    isLink: boolean
    timezone: string
}> = ({
    occurredAtUnixMs,
    occurredUntilUnixMs,
    wellId,
    searchInputTerm,
    isLink,
    timezone,
}) => {
    const formattedDate = highlightSearchTerms(
        getOccurredLabel(occurredAtUnixMs, occurredUntilUnixMs, timezone),
        searchInputTerm
    )

    const content = (
        <div className={styles.noteDate}>Occurred at: {formattedDate}</div>
    )

    if (isLink) {
        const iwpOverviewUrl = buildWellDetailsUrl(
            wellId,
            occurredAtUnixMs,
            timezone
        )
        return (
            <Link to={iwpOverviewUrl} className={styles.link}>
                {content}
            </Link>
        )
    }

    return content
}

// UpdateNoteBtn is a button that navigates to the update note modal for the given well and note ID.
const UpdateNoteBtn: FunctionComponent<{
    wellId: string
    noteId: string
}> = ({ wellId, noteId }) => {
    const navigate = useNavigate()

    return (
        <Button
            status="secondary"
            customButtonClassName={styles.updateActionBtn}
            condition="default"
            disabled={false}
            handleButtonClick={() =>
                navigate(
                    `${WELL_NOTES_ABS_ROUTE(
                        wellId
                    )}/${WELL_NOTES_UPDATE_CHILD_ROUTE(noteId)}`
                )
            }
        >
            Update
        </Button>
    )
}

// helpers

const getOccurredLabel = (
    occurredAtUnixMs: number,
    occurredUntilUnixMs: number | undefined,
    timezone: string
): string => {
    const occurredAtDay = dayjs(occurredAtUnixMs).tz(timezone)
    const occurredAt = occurredAtDay.format("MMM D, YYYY h:mm A")
    if (!occurredUntilUnixMs) {
        return occurredAt
    }
    const occurredUntilDay = dayjs(occurredUntilUnixMs).tz(timezone)
    if (occurredAtDay.isSame(occurredUntilDay, "day")) {
        const endTimeOnly = occurredUntilDay.format("h:mm A")
        return `${occurredAt} - ${endTimeOnly}`
    }
    const occurredUntil = occurredUntilDay.format("MMM D, YYYY h:mm A")
    return `${occurredAt} - ${occurredUntil}`
}

const formatWellNoteActorName = (fullName: string): string => {
    const normalizedFullName = fullName.trim().toLowerCase()
    if (normalizedFullName === "omni" || normalizedFullName === "system") {
        return "Support"
    }
    return fullName
}

export const highlightSearchTerms = (
    text: string,
    searchTerm: string
): ReactNode => {
    const trimmedSearch = searchTerm.trim()
    if (!trimmedSearch) {
        return text
    }

    try {
        // Escape special regex characters in the search term
        const escaped = trimmedSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

        // Build a case-insensitive regex to find the exact substring
        const regex = new RegExp(`(${escaped})`, "gi")

        // Split the text by the search term, keeping the matches using a capture group
        const parts = text.split(regex)

        // Wrap matching parts in <mark>, leave others unchanged
        return parts.map((part, index) =>
            part.toLowerCase() === trimmedSearch.toLowerCase() ? (
                <mark key={index}>{part}</mark>
            ) : (
                part
            )
        )
    } catch (e) {
        console.error("Error highlighting search terms:", e)
        return text
    }
}

// Generate the well details overview URL
// This function builds the URL for the well details page with the appropriate query parameters.
function buildWellDetailsUrl(
    wellId: string,
    occurredAt: number,
    timezone: string
): string {
    const noteTime = dayjs(occurredAt).tz(timezone)

    // motionDate should be start of day in profile timezone
    const motionDate = noteTime.startOf("day").valueOf()
    // toValue should be start of selected day in profile timezone
    const toValue = noteTime.startOf("day").valueOf()
    // fromValue should be 30 days before selected day in profile timezone
    const fromValue = noteTime.startOf("day").subtract(30, "day").valueOf()

    const url = `${WELL_DETAILS_ABS_ROUTE(wellId)}?${
        WellDetailsQryParams.motionDate
    }=${motionDate}&${WellDetailsQryParams.dateInterval}=day&${
        WellDetailsQryParams.from
    }=${fromValue}&${WellDetailsQryParams.to}=${toValue}`

    return url
}
